import React from "react";
import MuiTextField from "@material-ui/core/TextField";
import { Autocomplete } from "formik-material-ui-lab";
import { compose, defaultProps, withHooks } from "enhancers";
import { find, map, get, isNil } from "lodash";
import ListboxComponent from "./ListboxComponent";
import Hidden from "components/common/Hidden";
import Fuse from "fuse.js";

import InputAdornment from "@material-ui/core/InputAdornment";

import {
  borders,
  display,
  flexbox,
  palette,
  positions,
  shadows,
  sizing,
  spacing,
  typography,
} from "@material-ui/system";
import styled from "styled-components/macro";
import T from "../T";
import { replaceNameSpace } from "utils/helper";
import { Box, makeStyles } from "@material-ui/core";

const makeBoxProps = (Component) =>
  styled(Component)(
    borders,
    display,
    flexbox,
    palette,
    positions,
    shadows,
    sizing,
    spacing,
    typography
  );

const TextField = makeBoxProps(MuiTextField);

const useStyles = makeStyles({
  listbox: {
    padding: 0,
    "& ul": {
      padding: 0,
    },
    "& li": {},
  },
  paper: {
    margin: 0,
  },
});

export const enhancer = compose(
  defaultProps({
    options: [],
    forceFix: true,
  }),
  withHooks((props, hooks) => {
    const {
      label,
      options,
      fuse,
      transformDisplay,
      requiredLabel,
      onChange = () => {},
      ...restProps
    } = props;

    const { useMemo, useCallback, useEffect } = hooks;

    const customOptions = useMemo(() => {
      return map(options, "value");
    }, [options]);

    const freeSolo = props.freeSolo;
    const customGetOptionLabel = useCallback(
      (value) => {
        const item = find(options, { value });
        const label = item?.label || "";

        if (!label && freeSolo) {
          return value;
        } else {
          return label;
        }
      },
      [options, freeSolo]
    );

    const FormHelperTextProps = useMemo(
      () => ({
        component: (props) => {
          return (
            <Box style={{ marginTop: "1px" }}>
              <T variant="Helper/12" className={props.className}>
                {replaceNameSpace("error", props.children)}
              </T>
            </Box>
          );
        },
      }),
      []
    );

    const touched = get(props.form.touched, props.field.name);
    const error = get(props.form.errors, props.field.name);

    const value = props.field.value;

    const required = props.required;
    const helperText = props.helperText;
    const placeholder = props.placeholder;
    const icon = find(options, { value })?.icon;
    const renderInput = useCallback(
      (params) => {
        const inputProps = {
          ...params.inputProps,
          name: props.field.name,
        };

        const showError = touched && !!error;
        const displayHelperText = showError ? error : helperText;

        return (
          <TextField
            {...params}
            error={showError}
            helperText={displayHelperText}
            placeholder={placeholder}
            label={label}
            variant="outlined"
            InputLabelProps={{
              shrink: true,
              required: requiredLabel,
            }}
            value={value}
            required={required}
            InputProps={{
              ...params.InputProps,
              required: false,
              startAdornment: (
                <>
                  <Hidden when={!icon}>
                    <InputAdornment position="start">{icon}</InputAdornment>
                  </Hidden>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            inputProps={inputProps}
            FormHelperTextProps={FormHelperTextProps}
          />
        );
      },
      [
        touched,
        error,
        helperText,
        placeholder,
        label,
        requiredLabel,
        value,
        required,
        icon,
        FormHelperTextProps,
        props.field.name,
      ]
    );

    const filterOptions = useCallback(
      (options, { inputValue }) => {
        if (inputValue || options.length > 200) {
          options = new Fuse(props.options, {
            shouldSort: true,
            threshold: 0.6,
            location: 0,
            distance: 100,
            maxPatternLength: 32,
            minMatchCharLength: 1,
            keys: ["value"],
            id: "label",
          }).search(inputValue ?? "", { limit: 5 });
          options = map(options, "item.value");
        }
        return options;
      },
      [props.options]
    );

    const autoSelect =
      props.freeSolo && isNil(props.autoSelect) ? true : props.autoSelect;

    const classes = useStyles();

    const handleChange = useCallback(
      (event, newValue) => {
        onChange(event);
        // props.form.setFieldError(props.field.name, undefined);
        props.form.setFieldValue(props.field.name, newValue, false);
        props.form.setFieldTouched(props.field.name, true, false);
        setTimeout(() => {
          props.form.validateField(props.field.name);
        }, 0);
      },
      [onChange, props.field.name, props.form]
    );

    const handleBlur = useCallback(
      (event) => {
        const blurEvent = {
          ...event,
          target: {
            ...event?.target,
            name: props.field.name,
          },
        };

        props.field.onBlur(blurEvent);
        props.form.validateField(props.field.name);

        if (restProps.onBlur) {
          restProps.onBlur(event);
        }
      },
      [props.field, props.form, restProps]
    );

    return {
      key: props.forceFix ? value : undefined,
      ...restProps,
      name: props.field.name,
      options: [...customOptions],
      getOptionLabel: customGetOptionLabel,
      renderInput,
      ListboxComponent,
      filterOptions: fuse ? filterOptions : undefined,
      blurOnSelect: true,
      autoSelect,
      classes: { listbox: classes.listbox, paper: classes.paper },
      onChange: handleChange,
      onBlur: handleBlur,
    };
  })
);

export default enhancer(Autocomplete);
